import { zodResolver } from "@hookform/resolvers/zod";
import {
  type ApiResponsePage,
  TextractDocument,
} from "amazon-textract-response-parser";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { SubmitButton as Button } from "~/components/form/submit-button";
import TextBox from "~/components/form/text-box";
import {
  getTemplateModelFiles,
  saveTemplateDetails,
} from "~/services/supervisor.services";
import { cn } from "~/utils/tailwind";
import { createSkeletonSchema } from "~/utils/zod";

import { useNavigate } from "@tanstack/react-router";
import {
  templateEditHeaderRoute,
  templateEditTableRoute,
  templateRoute,
} from "~/pages/all-routes";
import CanvasComponent, { type CustomBlock } from "./canvas";

import ReplaceTextPopup from "./popup";

import PreviewHTML from "./index.html?raw";
import ReactDOM from "react-dom/client";
import PreviewTriggerComponent from "./preview";
import { toast } from "react-toastify";
import LoadingIcon from "~/components/form/LoadingIcon";

export interface IPopupProps {
  fieldId: number;
  value: string;
  trigger: HTMLElement;
  onChange: (value: string, popup: IPopupProps) => void;
}

const CropImageModelCollectionSchema = z.object({
  modelno: z.number(),
  tagid: z.number(),
  imageheight: z.number(),
  imagewidth: z.number(),
  x: z.number(),
  x0: z.number().optional(),
  x1: z.number().optional(),
  x2: z.number().optional(),
  x3: z.number().optional(),
  y: z.number(),
  y0: z.number().optional(),
  y1: z.number().optional(),
  y2: z.number().optional(),
  y3: z.number().optional(),
  match_tag: z.string().optional(),
  ReplaceText: z.string(),
  RegexText: z.string(),
  Id: z.string(),
  hardcodetext: z.string(),
  column_index: z.string(),
  tagtype: z.string(),
});

const CropImageModelSchema = z.object({
  CropImageModel: z.object({
    fileurl: z.string(),
    hint_1: z.string(),
    hint_2: z.string(),
    hint_3: z.string(),
    hint_4: z.string(),
    hint_5: z.string(),
    ignore_json_rows: z.string(),
    Jsonurl: z.string(),
    match_tag_1: z.string(),
    match_tag_2: z.string(),
    match_tag_3: z.string(),
    match_tag_4: z.string(),
    match_tag_5: z.string(),
    modeldesc: z.string(),
    modelno: z.string(),
    status: z.number().default(2),
    CropImageModelCollection: z.array(CropImageModelCollectionSchema),
  }),
});

type CropImageModelCollection = z.infer<typeof CropImageModelCollectionSchema>;
type CropImageModel = z.infer<typeof CropImageModelSchema>;

const TagButton = () => {
  return (
    <button type="button" className="absolute bottom-1 right-1 text-primary">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 16 16"
        fill="currentColor"
        className="size-4"
      >
        <path
          fillRule="evenodd"
          d="M4.5 2A2.5 2.5 0 0 0 2 4.5v2.879a2.5 2.5 0 0 0 .732 1.767l4.5 4.5a2.5 2.5 0 0 0 3.536 0l2.878-2.878a2.5 2.5 0 0 0 0-3.536l-4.5-4.5A2.5 2.5 0 0 0 7.38 2H4.5ZM5 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
          clipRule="evenodd"
        />
      </svg>
    </button>
  );
};

const TemplateEditHeaderComponent = () => {
  const navigate = useNavigate();
  const { id: templateId } = templateEditHeaderRoute.useSearch();
  const [textractData, setTextractData] = useState<TextractDocument>();
  const [templateData, setTemplateData] = useState<any>();
  const [tags, setTags] = useState<CropImageModelCollection[]>([]);
  const [activeField, setActiveField] = useState<Record<string, number>>({
    field: 0,
  });
  const [popup, setPopup] = useState<IPopupProps | null>(null);
  const [canvasLoading, setCanvasLoading] = useState(true);
  const triggerRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const PreviewWindowRef = useRef<Window | null>(null);
  const handleSetActiveField = (fieldId: number) => {
    setActiveField(() => {
      return { field: fieldId };
    });
  };

  const handleCanvasSelect = ({
    text,
    line,
  }: {
    text: string;
    line: CustomBlock;
  }) => {
    const fieldIndex = fields.findIndex(
      (field: any) => field.tagid === activeField?.field
    );
    if (fieldIndex !== -1 && text !== null && text.trim().length > 0) {
      const field: CropImageModelCollection = fields[fieldIndex];
      field.match_tag = text;
      field.x = line.left;
      field.y = line.top;
      field.imagewidth = line.width;
      field.imageheight = line.height;
      update(fieldIndex, field);
      handleSetActiveField(0);
    }
  };

  const handlePopupChange = (newValue: string, selectedPopup: IPopupProps) => {
    if (selectedPopup != null) {
      const fieldIndex = fields.findIndex(
        (field) => field.tagid === selectedPopup.fieldId
      );
      console.log(newValue, fieldIndex);

      if (fieldIndex !== -1) {
        const field = fields[fieldIndex];
        field.ReplaceText = newValue;

        console.log(
          field.ReplaceText,
          Array.of(field.ReplaceText, newValue).filter(Boolean).join(",")
        );
        setValue(
          `CropImageModel.CropImageModelCollection.${fieldIndex}`,
          field
        );
        void trigger(
          `CropImageModel.CropImageModelCollection.${fieldIndex}.ReplaceText`
        );
      }
    }
  };

  const handleTdClick = (template: any) => {
    const triggerElement = triggerRefs.current[template.tagid];
    const field = fields.find(
      (field: CropImageModelCollection) => field.tagid === template.tagid
    );
    if (triggerElement && field) {
      console.log(triggerElement, field);
      // not wrapping this in setTimeout will render the popup before the canvas is loaded
      setTimeout(() => {
        setPopup({
          fieldId: template.tagid,
          value: field.ReplaceText,
          trigger: triggerElement,
          onChange: handlePopupChange,
        });
      }, 0);
    }
  };

  const handleFieldDelete = (tagid: number) => {
    const templateFieldIndex = fields.findIndex(
      (field: CropImageModelCollection) => field.tagid === tagid
    );
    if (templateFieldIndex !== -1) {
      const field: CropImageModelCollection = fields[templateFieldIndex];
      field.match_tag = "";
      field.hardcodetext = "";
      field.x = 0;
      field.y = 0;
      field.imagewidth = 0;
      field.imageheight = 0;
      field.x0 = 0;
      field.y0 = 0;
      field.x1 = 0;
      field.y1 = 0;
      field.x2 = 0;
      field.y2 = 0;
      field.x3 = 0;
      field.y3 = 0;
      field.ReplaceText = "";
      field.Id = "";
      update(templateFieldIndex, field);
    }
  };

  const saveTemplateForm = useForm<CropImageModel>({
    resolver: zodResolver(CropImageModelSchema),
    defaultValues: {
      CropImageModel: {
        fileurl: "",
        hint_1: "",
        hint_2: "",
        hint_3: "",
        hint_4: "",
        hint_5: "",
        ignore_json_rows: "",
        Jsonurl: "",
        match_tag_1: "",
        match_tag_2: "",
        match_tag_3: "",
        match_tag_4: "",
        match_tag_5: "",
        modeldesc: "",
        modelno: "0",
        status: 2,
        CropImageModelCollection: tags ?? [],
      },
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = saveTemplateForm;

  console.log(errors);
  const { fields, update } = useFieldArray({
    control,
    name: "CropImageModel.CropImageModelCollection",
  });

  const onSubmit = async (data: CropImageModel) => {
    console.log(data);
    const { status, data: response } = await saveTemplateDetails(
      data,
      templateId.toString()
    );
    if (status) {
      console.log(response);
      toast.success("Template Headers Saved", {
        theme: "colored",
      });
    } else {
      toast.error("Error! Template Headers Not Saved.", {
        theme: "colored",
      });
    }
  };

  const onSaveSubmit = async (data: CropImageModel) => {
    data.CropImageModel.status = 3;
    const { status, data: response } = await saveTemplateDetails(
      data,
      templateId.toString()
    );
    if (status) {
      console.log(response);
      toast.success("Template Headers Save and Submitted", {
        theme: "colored",
      });
    } else {
      toast.error("Error! Template Headers Not Submitted.", {
        theme: "colored",
      });
    }
  };

  const handleSelection = useCallback(() => {
    const selection = window.getSelection();
    console.log(selection?.isCollapsed, selection?.rangeCount, activeField);
    if (!selection) {
      return;
    }
    let selected = selection?.getRangeAt(0)?.toString();
    if (
      selection.rangeCount > 0 &&
      activeField != null &&
      selected.trim().length > 0
    ) {
      // console.log(selection.getRangeAt(0).toString());
      selected = '"' + selected + '"';
      let fieldIndex = fields.findIndex(
        (field) => field.tagid === popup?.fieldId
      );
      if (fieldIndex === -1) {
        fieldIndex = fields.findIndex(
          (field) => field.tagid === activeField.field
        );
      }
      if (fieldIndex !== -1) {
        const field = fields[fieldIndex];
        if (field.ReplaceText.split(",").includes(selected)) return;
        field.ReplaceText = Array.of(field.ReplaceText, selected)
          .filter(Boolean)
          .join(",");
        setValue(
          `CropImageModel.CropImageModelCollection.${fieldIndex}`,
          field
        );
        void trigger(
          `CropImageModel.CropImageModelCollection.${fieldIndex}.ReplaceText`
        );
      }
    }
  }, [fields, popup?.fieldId, setValue, trigger, activeField]);
  useEffect(() => {
    try {
      void (async () => {
        async function getTemplateData(templateId: string) {
          const { status, data } = await getTemplateModelFiles(templateId);
          if (status) {
            const templateData: any = (data as any)?.data;
            if (templateData != null) {
              console.log(templateData);
              setTemplateData(templateData);
              const page1Response = await fetch(
                 (templateData["extractiondocuments"][0]
                      ?.json_presigned_url as string)
              );
              if (page1Response.ok) {
                const page1Data = await page1Response.json();
                const doc = new TextractDocument(page1Data as ApiResponsePage);
                setTextractData(doc);
              }
            }
          } else {
            toast.error("Server Error!Request Failed while loading data.", {
              theme: "colored",
            });
          }
        }
        if (templateId != null) {
          await getTemplateData(templateId.toString());
        }
      })();
    } catch (error) {
      console.log(error);
    }
  }, [setTemplateData, templateId]);

  useEffect(() => {
    if (templateData != null && templateData.tags.length > 0) {
      const values = getValues();
      type TemplateString =
        `CropImageModel.${keyof typeof values.CropImageModel}`;
      for (const key in templateData) {
        if (Object.prototype.hasOwnProperty.call(values.CropImageModel, key)) {
          const valueType =
            CropImageModelSchema.shape.CropImageModel.shape[
              key as keyof typeof values.CropImageModel
            ];
          if (valueType instanceof z.ZodString) {
            setValue(
              `CropImageModel.${key}` as TemplateString,
              templateData[key] + ""
            );
          } else if (valueType instanceof z.ZodNumber) {
            setValue(
              `CropImageModel.${key}` as TemplateString,
              templateData[key] as number
            );
          }
        }
      }
      const allTags: CropImageModelCollection[] = templateData.tags.map(
        (tag: any) => {
          const cropModelTag: CropImageModelCollection = createSkeletonSchema(
            CropImageModelCollectionSchema
          );
          cropModelTag.x = tag.x;
          cropModelTag.x0 = tag.x0;
          cropModelTag.x1 = tag.x1;
          cropModelTag.x2 = tag.x2;
          cropModelTag.x3 = tag.x3;
          cropModelTag.y = tag.y;
          cropModelTag.y0 = tag.y0;
          cropModelTag.y1 = tag.y1;
          cropModelTag.y2 = tag.y2;
          cropModelTag.y3 = tag.y3;
          cropModelTag.modelno = templateData.modelno;
          cropModelTag.tagid = tag.tagid;
          cropModelTag.column_index = "0";
          cropModelTag.match_tag = tag.match_tag;
          cropModelTag.hardcodetext = tag.hardcodetext;
          cropModelTag.imageheight = tag.imageheight;
          cropModelTag.imagewidth = tag.imagewidth;
          cropModelTag.ReplaceText = tag.replacetext;
          cropModelTag.tagtype = tag.tagtype;

          return cropModelTag;
        }
      );
      setTags(allTags);
      setValue("CropImageModel.CropImageModelCollection", allTags);
    }
  }, [getValues, setValue, templateData]);
  useEffect(() => {
    document.addEventListener("mouseup", handleSelection);
    document.addEventListener("keyup", handleSelection);
    return () => {
      document.removeEventListener("mouseup", handleSelection);
      document.removeEventListener("keyup", handleSelection);
    };
  }, [handleSelection]);

  const openPreviewWindow = () => {
    console.log(templateData, getValues());
    PreviewWindowRef.current = window.open(
      "/",
      "newWindow",
      "width=800,height=600,resizable=yes,scrollbars=yes"
    );
    const newWindowDocument = PreviewWindowRef.current?.document;
    newWindowDocument?.write(PreviewHTML);
    newWindowDocument?.close();

    if (newWindowDocument == null || PreviewWindowRef.current == null) return;

    ReactDOM.createRoot(newWindowDocument.getElementById("react-root")!).render(
      <PreviewTriggerComponent
        src={
          templateData.extractiondocuments[0].file_presigned_url
        }
        textractData={textractData! as any}
        templateData={getValues()}
        onSelect={handleCanvasSelect}
      />
    );
  };

  return (
    <div>
      {canvasLoading && <LoadingIcon />}
      <FormProvider {...saveTemplateForm}>
        <div className="bg-neutral-100 h-full shadow-md overflow-hidden">
          <div className="p-2 h-full">
            <form className="h-full" onSubmit={handleSubmit(onSubmit)}>
              <div className="flex flex-col h-full border-neutral-300 border-2">
                <div className="flex items-center justify-between p-2 border-b-2 border-b-neutral-300">
                  {templateData != null ? (
                    <div className="flex gap-2">
                      <div className="flex gap-2">
                        <div className="font-semibold">Model No:</div>
                        <div>{templateData.modelno}</div>
                      </div>
                      <div className="flex gap-2">
                        <div className="font-semibold">Model Description:</div>
                        <div>{templateData.modeldesc}</div>
                      </div>
                    </div>
                  ) : (
                    <>&nbsp;</>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="small"
                      type="button"
                      onClick={() => {
                        navigate({
                          to: templateRoute.fullPath,
                        });
                      }}
                    >
                      Back to menu
                    </Button>
                    <Button
                      size="small"
                      type="button"
                      onClick={openPreviewWindow}
                    >
                      Preview
                    </Button>
                    <Button size="small">Save</Button>
                    <Button
                      size="small"
                      type="button"
                      onClick={handleSubmit(onSaveSubmit)}
                    >
                      Save & Submit
                    </Button>
                    <Button
                      size="small"
                      type="button"
                      onClick={() => {
                        navigate({
                          to: templateEditTableRoute.fullPath,
                          search: { id: templateId },
                        });
                      }}
                    >
                      Table Extraction
                    </Button>
                  </div>
                </div>
                <div className="flex h-full overflow-hidden">
                  <div className="flex-grow w-2/6 overflow-y-scroll relative">
                    <table className="table-auto w-full">
                      <thead>
                        <tr>
                          <th className="border border-neutral-300 border-solid"></th>
                          <th className="font-semibold text-red-500 p-2 border border-neutral-300 border-solid">
                            Tag
                          </th>
                          <th className="font-semibold text-red-500 p-2 border border-neutral-300 border-solid">
                            Value
                          </th>
                          <th className="font-semibold text-red-500 p-2 border border-neutral-300 border-solid">
                            Fixed Value
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td
                            colSpan={4}
                            className="bg-blue-200 font-semibold border border-neutral-300 border-solid p-2"
                          >
                            Model Identifier
                          </td>
                        </tr>
                        {tags.length > 0 &&
                          templateData?.tags
                            ?.filter(
                              (template: any) => template.tagtype === "STATIC"
                            )
                            .map((template: any) => (
                              <tr
                                onClick={() => {
                                  handleSetActiveField(
                                    template?.tagid as number
                                  );
                                }}
                                key={template?.tagid}
                                className={cn(
                                  template?.tagid === activeField?.field &&
                                    "bg-yellow-200"
                                )}
                              >
                                <td className="border border-neutral-300 border-solid p-1">
                                  {(fields.find(
                                    (field: CropImageModelCollection) =>
                                      field.tagid === template.tagid
                                  )?.match_tag ||
                                    fields.find(
                                      (field: CropImageModelCollection) =>
                                        field.tagid === template.tagid
                                    )?.hardcodetext) && (
                                    <Button
                                      type="button"
                                      size={"small"}
                                      variant={"transparent"}
                                      onClick={() => {
                                        handleFieldDelete(
                                          template.tagid as number
                                        );
                                      }}
                                      className="text-red-500 font-bold p-1"
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={2}
                                        stroke="currentColor"
                                        className="size-5"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M6 18 18 6M6 6l12 12"
                                        />
                                      </svg>
                                    </Button>
                                  )}
                                </td>
                                <td className="text-center border border-neutral-300 border-solid p-2 cursor-pointer">
                                  {template.displayname}
                                </td>
                                <td
                                  className="text-center border border-neutral-300 border-solid p-2 relative"
                                  ref={(el) => {
                                    triggerRefs.current[template.tagid] = el;
                                  }}
                                  onClick={() => {
                                    if (
                                      fields.find(
                                        (field: CropImageModelCollection) =>
                                          field.tagid === template.tagid
                                      )?.match_tag
                                    ) {
                                      handleTdClick(template);
                                    }
                                  }}
                                >
                                  {
                                    fields.find(
                                      (field: CropImageModelCollection) =>
                                        field.tagid === template.tagid
                                    )?.match_tag
                                  }

                                  {fields.find(
                                    (field: CropImageModelCollection) =>
                                      field.tagid === template.tagid
                                  )?.ReplaceText ? (
                                    <TagButton />
                                  ) : null}
                                </td>
                                <td className="text-center border border-neutral-300 border-solid p-2">
                                  <TextBox
                                    className="w-full"
                                    name={`CropImageModel.CropImageModelCollection.${fields.findIndex(
                                      (f) => f.tagid === template.tagid
                                    )}.hardcodetext`}
                                    defaultValue={getValues(
                                      `CropImageModel.CropImageModelCollection.${fields.findIndex(
                                        (f) => f.tagid === template.tagid
                                      )}.hardcodetext`
                                    )}
                                  />
                                </td>
                              </tr>
                            ))}
                        <tr>
                          <td
                            colSpan={4}
                            className="bg-blue-200 font-semibold border border-neutral-300 border-solid p-2"
                          >
                            Model Tags
                          </td>
                        </tr>
                        {tags.length > 0 &&
                          templateData?.tags
                            ?.filter(
                              (template: any) => template.tagtype === "DYNAMIC"
                            )
                            .map((template: any) => (
                              <tr
                                onClick={() => {
                                  handleSetActiveField(
                                    template?.tagid as number
                                  );
                                }}
                                key={template?.tagid}
                                className={cn(
                                  template?.tagid === activeField?.field &&
                                    "bg-yellow-200"
                                )}
                              >
                                <td className="border border-neutral-300 border-solid p-1">
                                  {(fields.find(
                                    (field: CropImageModelCollection) =>
                                      field.tagid === template.tagid
                                  )?.match_tag ||
                                    fields.find(
                                      (field: CropImageModelCollection) =>
                                        field.tagid === template.tagid
                                    )?.hardcodetext) && (
                                    <Button
                                      type="button"
                                      size={"small"}
                                      variant={"transparent"}
                                      onClick={() => {
                                        handleFieldDelete(
                                          template.tagid as number
                                        );
                                      }}
                                      className="text-red-500 font-bold p-1"
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={2}
                                        stroke="currentColor"
                                        className="size-5"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M6 18 18 6M6 6l12 12"
                                        />
                                      </svg>
                                    </Button>
                                  )}
                                </td>
                                <td
                                  onClick={() => {
                                    handleSetActiveField(
                                      template?.tagid as number
                                    );
                                  }}
                                  className="text-center border border-neutral-300 border-solid p-2 cursor-pointer"
                                >
                                  {template.displayname}
                                </td>
                                <td
                                  className="text-center border border-neutral-300 border-solid p-2 relative"
                                  ref={(el) => {
                                    triggerRefs.current[template.tagid] = el;
                                  }}
                                  onClick={() => {
                                    if (
                                      fields.find(
                                        (field: CropImageModelCollection) =>
                                          field.tagid === template.tagid
                                      )?.match_tag
                                    ) {
                                      handleTdClick(template);
                                    }
                                  }}
                                >
                                  {
                                    fields.find(
                                      (field: CropImageModelCollection) =>
                                        field.tagid === template.tagid
                                    )?.match_tag
                                  }

                                  {fields.find(
                                    (field: CropImageModelCollection) =>
                                      field.tagid === template.tagid
                                  )?.ReplaceText ? (
                                    <TagButton />
                                  ) : null}
                                </td>
                                <td className="text-center border border-neutral-300 border-solid p-2">
                                  <TextBox
                                    className="w-full"
                                    name={`CropImageModel.CropImageModelCollection.${fields.findIndex(
                                      (f) => f.tagid === template.tagid
                                    )}.hardcodetext`}
                                    defaultValue={getValues(
                                      `CropImageModel.CropImageModelCollection.${fields.findIndex(
                                        (f) => f.tagid === template.tagid
                                      )}.hardcodetext`
                                    )}
                                  />
                                </td>
                              </tr>
                            ))}
                      </tbody>
                    </table>
                    {popup != null && (
                      <ReplaceTextPopup
                        popup={popup}
                        onClose={() => {
                          setPopup(null);
                        }}
                        onChange={popup?.onChange}
                      />
                    )}
                  </div>
                  <div className="w-4/6 overflow-x-hidden overflow-y-scroll">
                    {textractData != null && (
                      <CanvasComponent
                        src={
                           templateData.extractiondocuments[0]
                                .file_presigned_url
                        }
                        textractData={textractData}
                        onSelect={handleCanvasSelect}
                        setCanvasLoading={setCanvasLoading}
                      />
                    )}
                  </div>
                  <div></div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};

export default TemplateEditHeaderComponent;
